import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from '@node-space/ui-components'
import { isLocal } from 'utils/environment'

interface BffHotReloadState {
  ws: WebSocket | null
  reconnectTimeout: ReturnType<typeof setTimeout> | undefined
  lastServerTimestamp: number | null
  subscribers: Set<() => void>
  isConnecting: boolean
}

declare global {
  // eslint-disable-next-line no-var
  var __bffHotReload: BffHotReloadState | undefined
}

function getState(): BffHotReloadState {
  globalThis.__bffHotReload ??= {
    ws: null,
    reconnectTimeout: undefined,
    lastServerTimestamp: null,
    subscribers: new Set(),
    isConnecting: false,
  }
  return globalThis.__bffHotReload
}

const SHELL_QUERY_KEYS = new Set(['profile', 'accounts', 'verification'])

export function useBffHotReload() {
  const queryClient = useQueryClient()
  const isSubscribedRef = useRef(false)
  const invalidateCacheRef = useRef(() => {
    queryClient.invalidateQueries({
      // Background refetch of everything except the page-shell queries, so the
      // layout/profile stay mounted and the page doesn't flash a loader.
      refetchType: 'active',
      predicate: query => !SHELL_QUERY_KEYS.has(query.queryKey[0] as string),
    })
  })

  useEffect(() => {
    if (!isLocal) {
      return
    }

    const state = getState()
    const callback = invalidateCacheRef.current

    if (!isSubscribedRef.current) {
      state.subscribers.add(callback)
      isSubscribedRef.current = true

      if (state.subscribers.size === 1 && !state.ws && !state.isConnecting) {
        connect()
      }
    }

    return () => {
      const cleanupState = getState()
      cleanupState.subscribers.delete(callback)
      isSubscribedRef.current = false

      if (cleanupState.subscribers.size === 0) {
        if (cleanupState.reconnectTimeout) {
          clearTimeout(cleanupState.reconnectTimeout)
          cleanupState.reconnectTimeout = undefined
        }

        if (cleanupState.ws) {
          cleanupState.ws.close()
          cleanupState.ws = null
        }
      }
    }
  }, [queryClient])
}

function connect() {
  const state = getState()

  if (state.isConnecting || state.ws) {
    return
  }

  state.isConnecting = true

  try {
    const wsProtocol = globalThis.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const ws = new WebSocket(`${wsProtocol}//${globalThis.location.host}/ws/dev-reload`)
    state.ws = ws

    ws.onopen = () => {
      state.isConnecting = false
    }

    ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data)

        if (data.type === 'reload') {
          showToast()
          state.subscribers.forEach(cb => cb())
          state.lastServerTimestamp = data.timestamp
        } else if (data.type === 'connected') {
          if (state.lastServerTimestamp !== null && data.timestamp !== state.lastServerTimestamp) {
            showToast()
            state.subscribers.forEach(cb => cb())
          }
          state.lastServerTimestamp = data.timestamp
        }
      } catch {
        // ignore malformed messages
      }
    }

    ws.onerror = () => {
      state.isConnecting = false
    }

    ws.onclose = () => {
      state.ws = null
      state.isConnecting = false

      if (state.subscribers.size > 0) {
        state.reconnectTimeout = setTimeout(connect, 1000)
      }
    }
  } catch {
    state.isConnecting = false

    if (state.subscribers.size > 0) {
      state.reconnectTimeout = setTimeout(connect, 2000)
    }
  }
}

function showToast() {
  toast({
    header: 'BFF Changes Detected',
    body: 'Refetching client data...',
    variant: 'info',
    duration: 5000,
  })
}
